import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { MessageSquare, Lightbulb, Megaphone, Send, Check } from "lucide-react";

const KINDS = [
  { id: "comment", label: "Comment", icon: <MessageSquare size={14} /> },
  { id: "suggestion", label: "Suggestion", icon: <Lightbulb size={14} /> },
  { id: "shoutout", label: "Shout-out", icon: <Megaphone size={14} /> },
];

export default function CommentPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    kind: "comment",
    city: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        kind: form.kind,
        message: form.message,
        name: form.name.trim() || undefined,
        email: form.email.trim() || undefined,
        city: form.city.trim() || undefined,
      };
      await api.post("/comments", payload);
      toast("Got it. Loud and clear.");
      setSent(true);
    } catch (e2) {
      toast(formatApiError(e2?.response?.data?.detail) || "Could not send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="comment-page">
      <section className="relative grain splatter overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-8 pt-16 pb-16 grid md:grid-cols-12 gap-10 relative z-10">
          <div className="md:col-span-5">
            <div className="overline">Comment · Suggest · Shout</div>
            <h1 className="font-display text-6xl md:text-7xl uppercase mt-3 leading-[0.85] drip-shadow">
              Talk to <span className="text-[#FF1493]">us.</span>
            </h1>
            <p className="text-white/75 mt-7 text-lg max-w-md">
              Hot take? Idea for a city stop? Brand to feature? Random hello? Drop it. We read
              every one — the best ones turn into actual content.
            </p>
            <div className="mt-8 space-y-3">
              {KINDS.map((k) => (
                <div key={k.id} className="flex items-center gap-3 text-sm text-white/70">
                  <span className="text-[#FF1493]">{k.icon}</span>
                  <span className="font-display tracking-[0.18em] uppercase">{k.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            {sent ? (
              <div
                className="border border-[#FFC107]/40 bg-[#141414] p-10 text-center"
                data-testid="comment-success"
              >
                <div className="w-14 h-14 mx-auto bg-[#FFC107] text-[#0A0A0A] flex items-center justify-center">
                  <Check size={26} />
                </div>
                <div className="overline mt-5">Received</div>
                <h3 className="font-display text-3xl uppercase mt-3">Heard you.</h3>
                <p className="text-white/70 text-sm mt-3 max-w-sm mx-auto">
                  Every message gets read on the road. The best ones show up in the next dispatch.
                </p>
                <button
                  onClick={() => {
                    setSent(false);
                    setForm({ name: "", email: "", kind: "comment", city: "", message: "" });
                  }}
                  className="btn-ghost mt-7"
                  data-testid="comment-another"
                >
                  Send another
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="tac-card p-7 space-y-3" data-testid="comment-form">
                <div className="overline">What you got?</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {KINDS.map((k) => (
                    <button
                      key={k.id}
                      type="button"
                      onClick={() => setForm({ ...form, kind: k.id })}
                      className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.18em] font-display transition ${
                        form.kind === k.id
                          ? "bg-[#FF1493] text-white"
                          : "border border-white/10 text-white/70 hover:text-white"
                      }`}
                      data-testid={`comment-kind-${k.id}`}
                    >
                      {k.icon} {k.label}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-3 mt-3">
                  <input
                    placeholder="Your name (optional)"
                    className="tac-input"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="comment-name"
                  />
                  <input
                    type="email"
                    placeholder="Email (optional)"
                    className="tac-input"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="comment-email"
                  />
                </div>
                <input
                  placeholder="City (optional)"
                  className="tac-input"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  data-testid="comment-city"
                />
                <textarea
                  required
                  rows={6}
                  placeholder="Spill it..."
                  className="tac-input"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="comment-message"
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="btn-blaze w-full justify-center"
                  data-testid="comment-submit"
                >
                  <Send size={14} /> {submitting ? "Sending..." : "Send it"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
